import type { IntlShape } from "react-intl";
import { GroupingConferenceDivision } from "~pages/Standings/enums/groupingConferenceDivision";
import { GroupingStagePool } from "~pages/Standings/enums/groupingStagePool";
import type { StandingTransformed } from "~pages/Standings/types/standingTransformed";
import type { TransformedResult } from "~pages/Standings/types/transformedResult";
import { getGroupStandings } from "~pages/Standings/utilities/getGroupStandings";
import type { StandingResources, StandingResponse } from "~types/standing";

const getStandings = (response: StandingResponse) => {
  const resources: StandingResources | undefined = response?.includes?.resources;
  return (response?.data || []).map((standing) => ({
    position: standing.position,
    standingId: standing.standingId,
    standingConfigurationId: standing.standingConfigurationId,
    calculated: standing.calculated,
    stageCode: standing.stageCode,
    poolCode: standing.poolCode,
    standingConfiguration: standing.standingConfiguration,
    groupingBase: standing.groupingBase,
    points: standing.points,
    entityId: standing.entityId,
    groupingConferenceDivision: standing.groupingConferenceDivision,
    groupingStagePool: standing.groupingStagePool,
    conferenceId: standing.conferenceId,
    divisionId: standing.divisionId,
    stage: {
      ...standing.stage,
      ...resources?.[standing?.stage?.resourceType ?? ""]?.[standing?.stage?.id ?? ""],
    },
    pool: {
      ...standing.pool,
      ...resources?.[standing?.pool?.resourceType ?? ""]?.[standing?.pool?.id ?? ""],
    },
    conference: {
      ...standing.conference,
      ...resources?.[standing?.conference?.resourceType ?? ""]?.[standing?.conference?.id ?? ""],
    },
    division: {
      ...standing.division,
      ...resources?.[standing?.division?.resourceType ?? ""]?.[standing?.division?.id ?? ""],
    },
    entity: resources?.[standing?.entity?.resourceType ?? ""]?.[standing?.entity?.id],
  }));
};

const transformConferenceDivision = (
  response: StandingResponse,
  conferenceGrouping: GroupingConferenceDivision,
  formatMessage: IntlShape["formatMessage"],
) => {
  const resources: StandingResources | undefined = response?.includes?.resources;
  const standings = getStandings(response);
  const result: Array<TransformedResult<StandingTransformed | TransformedResult<StandingTransformed>>> = [];
  const groupedByConference = getGroupStandings(standings, "conference.id");
  for (const [key, values] of Object.entries<StandingTransformed[]>(groupedByConference.data)) {
    let conferenceValues: Array<TransformedResult<StandingTransformed>> | StandingTransformed[] = values;

    if (conferenceGrouping === GroupingConferenceDivision.Division) {
      const divisionValuesResult: Array<TransformedResult<StandingTransformed>> = [];
      const groupedByDivision = getGroupStandings(values, "division.id");

      for (const [divisionKey, divisionValues] of Object.entries<StandingTransformed[]>(groupedByDivision.data)) {
        const division = resources?.divisions?.[divisionKey] || {
          nameLocal: divisionKey,
          divisionId: divisionKey,
        };

        divisionValuesResult.push({
          entity: { name: division.nameLocal, id: division.divisionId },
          values: divisionValues,
        });
      }
      if (groupedByDivision.noKeyData.length > 0) {
        divisionValuesResult.push({
          entity: {
            name: formatMessage({ id: "standings.not.in.division" }),
            id: "noKeyData",
          },
          values: groupedByDivision.noKeyData,
        });
      }
      conferenceValues = divisionValuesResult;
    }

    const conference = resources?.conferences?.[key] || {
      nameLocal: key,
      conferenceId: key,
    };
    result.push({
      entity: { name: conference.nameLocal, id: conference.conferenceId },
      values: conferenceValues,
    });
  }
  if (groupedByConference.noKeyData.length > 0) {
    result.push({
      entity: { name: formatMessage({ id: "standings.not.in.conference" }), id: "noKeyData" },
      values: groupedByConference.noKeyData,
    });
  }
  return result;
};

const transformStagePool = (response: StandingResponse, grouping: GroupingStagePool, formatMessage: IntlShape["formatMessage"]) => {
  const resources: StandingResources | undefined = response?.includes?.resources;
  const standings = getStandings(response);
  const result: Array<TransformedResult<StandingTransformed | TransformedResult<StandingTransformed>>> = [];

  const groupedByStage = getGroupStandings(standings, "stage.id");
  for (const [key, values] of Object.entries<StandingTransformed[]>(groupedByStage.data)) {
    let stageValues: Array<TransformedResult<StandingTransformed>> | StandingTransformed[] = values;

    if (grouping === GroupingStagePool.StagePool) {
      const poolValuesResult: Array<TransformedResult<StandingTransformed>> = [];
      const groupedByPool = getGroupStandings(values, "pool.id");
      for (const [poolKey, poolValues] of Object.entries<StandingTransformed[]>(groupedByPool.data)) {
        const pool = resources?.seasonPools?.[poolKey] || {
          nameLocal: poolKey,
          poolCode: poolKey,
        };

        poolValuesResult.push({
          entity: { name: pool.nameLocal, id: pool.poolCode },
          values: poolValues,
        });
      }
      if (groupedByPool.noKeyData.length > 0) {
        poolValuesResult.push({
          entity: {
            name: formatMessage({ id: "standings.not.in.pool" }),
            id: "noKeyData",
          },
          values: groupedByPool.noKeyData,
        });
      }
      stageValues = poolValuesResult;
    }

    const stage = resources?.seasonStages?.[key] || {
      nameLocal: key,
      stageCode: key,
    };
    result.push({
      entity: { name: stage.nameLocal, id: stage.stageCode },
      values: stageValues,
    });
  }
  if (groupedByStage.noKeyData.length > 0) {
    result.push({
      entity: {
        name: formatMessage({ id: "standings.not.in.stage" }),
        id: "noKeyData",
      },
      values: groupedByStage.noKeyData,
    });
  }

  return result;
};

export const transformStandingResponse = (
  response: StandingResponse,
  formatMessage: IntlShape["formatMessage"],
  grouping?: GroupingStagePool,
  conferenceGrouping?: GroupingConferenceDivision,
) => {
  if (grouping === GroupingStagePool.StagePool || grouping === GroupingStagePool.Stage) {
    return transformStagePool(response, grouping, formatMessage);
  }
  if (conferenceGrouping === GroupingConferenceDivision.Division || conferenceGrouping === GroupingConferenceDivision.Conference) {
    return transformConferenceDivision(response, conferenceGrouping, formatMessage);
  }
  if (conferenceGrouping === GroupingConferenceDivision.Overall && grouping === GroupingStagePool.Overall) {
    return getStandings(response);
  }
};
